/**
 * Boundary adapters for the managed-content resources.
 *
 * `/api/admin/content/pages` and `/api/admin/news` are both typed `PagedModel`
 * in the OpenAPI document, whose `content` is an array with **no item
 * schema**, so nothing about those rows is guaranteed by the contract. They
 * are narrowed here rather than asserted at the call site, the same way
 * `src/services/communicationsCenter/adapters.ts` narrows the recipient
 * payload. `/api/admin/content/banners` does declare its item type, but it is
 * put through the same funnel so one malformed row cannot take down a
 * listing.
 */

import {
  BannerPlacement,
  BannerView,
  ContentPagedResponse,
  ContentPageItemView,
  ContentPageView,
  NewsView,
  PageType,
  BANNER_PLACEMENTS,
  PAGE_TYPES,
} from "@/types/content";
import { FileView } from "@/types/file";

const asRecord = (raw: unknown): Record<string, unknown> | null =>
  raw != null && typeof raw === "object"
    ? (raw as Record<string, unknown>)
    : null;

const readString = (
  source: Record<string, unknown>,
  key: string,
): string | undefined => {
  const value = source[key];
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
};

const readNumber = (
  source: Record<string, unknown>,
  key: string,
): number | undefined => {
  const value = source[key];
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
};

const readPageType = (
  source: Record<string, unknown>,
): PageType | undefined => {
  const value = source.pageType;
  return typeof value === "string" &&
    (PAGE_TYPES as readonly string[]).includes(value)
    ? (value as PageType)
    : undefined;
};

const readPlacement = (
  source: Record<string, unknown>,
): BannerPlacement | undefined => {
  const value = source.placement;
  return typeof value === "string" &&
    (BANNER_PLACEMENTS as readonly string[]).includes(value)
    ? (value as BannerPlacement)
    : undefined;
};

/** A file is only usable once it has an id; anything else renders as absent. */
export const adaptFile = (raw: unknown): FileView | undefined => {
  const source = asRecord(raw);
  if (!source) return undefined;

  const id = readNumber(source, "id");
  if (id == null) return undefined;

  const type = asRecord(source.type);

  return {
    id,
    type: type
      ? {
          id: readNumber(type, "id") ?? 0,
          code: readString(type, "code"),
          value: readString(type, "value"),
        }
      : undefined,
    name: readString(source, "name"),
    extension: readString(source, "extension"),
    description: readString(source, "description"),
    uploadedOn: readString(source, "uploadedOn"),
    presignedUrl: readString(source, "presignedUrl"),
    presignedExpiresOn: readString(source, "presignedExpiresOn"),
  };
};

export const adaptBanner = (raw: unknown): BannerView | null => {
  const source = asRecord(raw);
  if (!source) return null;

  const id = readNumber(source, "id");
  if (id == null) return null;

  return {
    id,
    file: adaptFile(source.file),
    title: readString(source, "title"),
    subtitle: readString(source, "subtitle"),
    pageType: readPageType(source),
    placement: readPlacement(source),
    buttonText: readString(source, "buttonText"),
    buttonUrl: readString(source, "buttonUrl"),
    buttonAlt: readString(source, "buttonAlt"),
    createdOn: readString(source, "createdOn"),
    updatedOn: readString(source, "updatedOn"),
  };
};

/**
 * Page items have no id of their own on write and may come back without one,
 * so an item is kept as long as it carries any content at all — dropping it
 * would silently shrink a page the next time it is saved.
 */
export const adaptContentPageItem = (
  raw: unknown,
  index: number,
): ContentPageItemView | null => {
  const source = asRecord(raw);
  if (!source) return null;

  const title = readString(source, "title");
  const description = readString(source, "description");
  const file = adaptFile(source.file);
  if (title == null && description == null && file == null) return null;

  return {
    id: readNumber(source, "id"),
    file,
    title,
    description,
    sortOrder: readNumber(source, "sortOrder") ?? index,
  };
};

export const adaptContentPage = (raw: unknown): ContentPageView | null => {
  const source = asRecord(raw);
  if (!source) return null;

  const id = readNumber(source, "id");
  if (id == null) return null;

  const rawItems = Array.isArray(source.items) ? source.items : [];

  return {
    id,
    pageType: readPageType(source),
    title: readString(source, "title"),
    description: readString(source, "description"),
    items: rawItems
      .map(adaptContentPageItem)
      .filter((item): item is ContentPageItemView => item != null)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    createdOn: readString(source, "createdOn"),
    updatedOn: readString(source, "updatedOn"),
  };
};

export const adaptNews = (raw: unknown): NewsView | null => {
  const source = asRecord(raw);
  if (!source) return null;

  const id = readNumber(source, "id");
  if (id == null) return null;

  return {
    id,
    title: readString(source, "title"),
    content: readString(source, "content"),
    file: adaptFile(source.file),
    /*
     * `NewsView` does not declare `shareUrl` even though `NewsDTO` accepts one,
     * so this reads whatever the payload happens to carry instead of assuming
     * the round trip preserves it.
     */
    shareUrl: readString(source, "shareUrl"),
    createdOn: readString(source, "createdOn"),
    updatedOn: readString(source, "updatedOn"),
  };
};

/**
 * Narrow a paged payload, dropping rows the adapter cannot make sense of.
 *
 * The totals come from the response rather than from the surviving rows: they
 * drive pagination, and recomputing them from a filtered array would make the
 * last page unreachable whenever a row is dropped.
 */
export const adaptPagedContent = <T>(
  raw: unknown,
  adapt: (entry: unknown, index: number) => T | null,
  requestedSize: number,
): ContentPagedResponse<T> => {
  const source = asRecord(raw);
  const rawContent =
    source && Array.isArray(source.content) ? source.content : [];
  const content = rawContent
    .map(adapt)
    .filter((entry): entry is T => entry != null);

  const page = asRecord(source?.page);

  return {
    content,
    page: {
      size: page ? (readNumber(page, "size") ?? requestedSize) : requestedSize,
      number: page ? (readNumber(page, "number") ?? 0) : 0,
      totalElements: page
        ? (readNumber(page, "totalElements") ?? content.length)
        : content.length,
      totalPages: page
        ? (readNumber(page, "totalPages") ?? (content.length > 0 ? 1 : 0))
        : content.length > 0
          ? 1
          : 0,
    },
  };
};
